import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ColorBrandBlue1, ColorNordicGrey1, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import Card from 'components/generic/card';
import Tag, { type TagProps } from 'components/generic/tag';

export type CardWithTagProps = {
    tags?: TagProps[];
    selected?: boolean;
    disabled?: boolean;
    cardAs?: 'label';
    children: ReactNode;
};

const Wrapper = styled.div<{ $hasTag: boolean }>`
    position: relative;

    ${({ $hasTag }) =>
        $hasTag &&
        css`
            padding-top: ${({ theme }) => theme.Spacing12};
        `}
`;

const StyledCard = styled(Card)<CardWithTagProps>`
    position: unset;
    margin-bottom: 0;
    border: 1px solid ${({ selected }) => (selected ? ColorBrandBlue1 : ColorNordicGrey1)};
    transition: opacity 300ms ease;
    padding: ${Spacing16};

    &:focus-within {
        border: 1px solid ${ColorBrandBlue1};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
            transition: opacity 300ms ease;

            &:after {
                background-color: transparent;
            }
        `}
`;

const TagContainer = styled.div` 
    position: absolute;
    top: 0;
    left: ${Spacing16};
    display: flex;
    gap: ${Spacing8};
`;

export default function CardWithTag(props: CardWithTagProps) {
    const { tags, selected, disabled, cardAs, children, ...restProps } = props;
    const [primaryTag, secondaryTag] = tags || [];

    return (
        <Wrapper $hasTag={!!tags?.length} {...restProps}>
            <StyledCard selected={!!selected} forwardedAs={cardAs} disabled={disabled}>
                {!!tags?.length && (
                    <TagContainer>
                        {primaryTag && <Tag {...primaryTag} />}
                        {secondaryTag && <Tag {...secondaryTag} />}
                    </TagContainer>
                )}
                {children}
            </StyledCard>
        </Wrapper>
    );
}
